import { useEffect, useState } from 'react';
import { useOrg } from '../../../context/OrgContext';
import { getInstructorAnalyticsApi } from '../../../api/apiServices';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

// Course prices are stored in the same base unit used across the admin
// course list (see admin/courses/page.jsx), which displays them as Naira
// at a fixed ~1400/unit rate -- reused here so a course's revenue reads the
// same way wherever it's shown.
const NAIRA_RATE = 1400;
const formatNaira = (amount) => `₦${Math.round((amount || 0) * NAIRA_RATE).toLocaleString('en-NG')}`;

const StatCard = ({ icon, label, value, color }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color}`}>
      <i className={icon} />
    </div>
    <p className="text-2xl font-bold text-slate-900">{value}</p>
    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
  </div>
);

export default function AdminAnalyticsPage() {
  const { currentOrgId, currentOrg } = useOrg();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    getInstructorAnalyticsApi(currentOrgId ? { organizationId: currentOrgId } : {})
      .then((res) => {
        if (res.success) setData(res);
        else setError(res.message || 'Failed to load analytics');
      })
      .catch((err) => {
        console.error('Failed to load analytics:', err);
        setError(err.response?.data?.message || 'Failed to load analytics');
      })
      .finally(() => setLoading(false));
  }, [currentOrgId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl p-6 text-sm font-medium">
        {error}
      </div>
    );
  }

  const stats = data?.stats || {};
  const trend = data?.trend || [];
  const topCourses = data?.topCourses || [];

  return (
    <div className="max-w-6xl">
      {currentOrg && (
        <div className="mb-6 px-4 py-2.5 bg-purple-50 border border-purple-100 rounded-2xl text-sm text-purple-700 font-medium inline-flex items-center gap-2">
          <i className="ri-building-2-line" /> Showing analytics for {currentOrg.name} only
        </div>
      )}

      {/* Summary cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon="ri-book-3-line text-teal-600" label="Courses" value={stats.totalCourses ?? 0} color="bg-teal-50" />
        <StatCard icon="ri-group-line text-indigo-600" label="Students" value={(stats.totalStudents ?? 0).toLocaleString()} color="bg-indigo-50" />
        <StatCard icon="ri-trophy-line text-amber-600" label="Quiz Pass Rate" value={`${stats.quizPassRate ?? 100}%`} color="bg-amber-50" />
        <StatCard icon="ri-money-dollar-circle-line text-emerald-600" label="Estimated Revenue" value={formatNaira(stats.totalRevenue)} color="bg-emerald-50" />
      </div>

      {/* Trend charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          <h3 className="text-sm font-bold text-slate-800 mb-4">Enrollments (last 6 months)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={28} />
              <Tooltip cursor={{ fill: '#f8fafc' }} />
              <Bar dataKey="enrollments" fill="#0d9488" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          <h3 className="text-sm font-bold text-slate-800 mb-4">Estimated Revenue (last 6 months)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend.map((t) => ({ ...t, revenueNaira: Math.round((t.revenue || 0) * NAIRA_RATE) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                width={50}
                tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip formatter={(v) => [`₦${Number(v).toLocaleString('en-NG')}`, 'Revenue']} />
              <Line type="monotone" dataKey="revenueNaira" stroke="#0d9488" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top courses */}
      <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-800">Top Courses by Enrollment</h3>
        </div>
        {topCourses.length === 0 ? (
          <p className="px-6 py-8 text-sm text-slate-400 text-center">No enrollments yet</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Course</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Enrollments</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Est. Revenue</th>
              </tr>
            </thead>
            <tbody>
              {topCourses.map((c) => (
                <tr key={c._id} className="border-b border-slate-50 last:border-0">
                  <td className="px-6 py-3.5 text-sm font-medium text-slate-800">{c.title}</td>
                  <td className="px-4 py-3.5 text-sm text-slate-600">{c.enrollments}</td>
                  <td className="px-4 py-3.5 text-sm text-slate-600">{formatNaira(c.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
